import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface HelicopterIcon {
  id: string;
  manufacturer: string;
  model: string;
  icon_url: string | null;
}

interface HelicopterIconMatch {
  typeRating: string;
  iconUrl: string | null;
  manufacturer: string;
  model: string;
}

// Fetch helicopter icons with caching via React Query
const fetchHelicopterIcons = async (): Promise<HelicopterIcon[]> => {
  const { data, error } = await supabase
    .from('helicopters')
    .select('id, manufacturer, model, icon_url')
    .not('icon_url', 'is', null);
  
  if (error) {
    console.error('Error fetching helicopter icons:', error);
    return [];
  }
  return data || [];
};

/**
 * Normalize a type rating string for better matching
 * Strips percentages, parentheses, and extra qualifiers
 */
function normalizeRating(rating: string): string {
  return rating
    .toLowerCase()
    .trim()
    // Remove percentages like "80-100%"
    .replace(/\d+-?\d*%/g, '')
    // Remove parenthetical content
    .replace(/\([^)]*\)/g, '')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Hook to fetch helicopter icons for type ratings
 * Uses React Query for global caching - fetches once and reuses across all components
 */
export function useHelicopterIcons(typeRatings: string[] = []) {
  // Use React Query for cached fetching - 24 hour stale time
  const { data: helicopterData = [], isLoading } = useQuery({
    queryKey: ['helicopters-with-icons'],
    queryFn: fetchHelicopterIcons,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
  });

  // Match type ratings to helicopters - memoized for performance
  const icons = useMemo<HelicopterIconMatch[]>(() => {
    if (!typeRatings.length || !helicopterData.length) {
      return [];
    }

    return typeRatings.map(rating => {
      const ratingNormalized = normalizeRating(rating);
      
      // Try to find a matching helicopter
      const match = helicopterData.find(heli => {
        const fullName = `${heli.manufacturer} ${heli.model}`.toLowerCase();
        const modelOnly = heli.model.toLowerCase();
        const manuLower = heli.manufacturer.toLowerCase();
        
        // Exact match on full name
        if (fullName === ratingNormalized) return true;
        
        // Model match (e.g., "H125" matches "Airbus H125")
        if (modelOnly === ratingNormalized) return true;
        
        // Partial match - normalized rating contains model or vice versa
        if (ratingNormalized.includes(modelOnly) || modelOnly.includes(ratingNormalized)) return true;
        
        // Check if rating contains both manufacturer and model parts
        if (ratingNormalized.includes(manuLower) && ratingNormalized.includes(modelOnly)) return true;
        
        // Also try original rating for exact matches
        const ratingLower = rating.toLowerCase().trim();
        if (fullName === ratingLower || modelOnly === ratingLower) return true;
        if (ratingLower.includes(modelOnly)) return true;
        
        return false;
      });

      return {
        typeRating: rating,
        iconUrl: match?.icon_url || null,
        manufacturer: match?.manufacturer || '',
        model: match?.model || '',
      };
    });
  }, [typeRatings, helicopterData]);

  return { icons, loading: isLoading, helicopterData };
}

/**
 * Utility function to get a single helicopter icon by type rating string
 * For use in components that don't need the full hook
 */
export function findHelicopterIcon(
  typeRating: string,
  helicopterData: HelicopterIcon[]
): HelicopterIcon | null {
  const ratingNormalized = normalizeRating(typeRating);
  const ratingLower = typeRating.toLowerCase().trim();
  
  return helicopterData.find(heli => {
    const fullName = `${heli.manufacturer} ${heli.model}`.toLowerCase();
    const modelOnly = heli.model.toLowerCase();
    const manuLower = heli.manufacturer.toLowerCase();
    
    if (fullName === ratingNormalized || fullName === ratingLower) return true;
    if (modelOnly === ratingNormalized || modelOnly === ratingLower) return true;
    if (ratingNormalized.includes(modelOnly) || ratingLower.includes(modelOnly)) return true;
    if (ratingNormalized.includes(manuLower) && ratingNormalized.includes(modelOnly)) return true;
    
    return false;
  }) || null;
}
